import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { ArchiveEntry, BidGamemode, Declaration, ExpDeclaration, GameEvents, Profile, RoundState } from "@/server-types";
import { cardComparator } from "@/tools/cards";
import * as http from "@/tools/http";
import * as ws from "@/tools/websocket";
import type { position } from "@/types";

interface Zone {
	username: string;
	profile?: Profile;
	cards: string[];
	playableCards?: string[];
	message?: string;
	playedCard?: string;
}

interface PlayedCard {
	value: string;
	position: position;
	zIndex: number;
}

interface DealFlight {
	id: number;
	target: zone_k;
	delay: number;
}

interface Game {
	points: [number, number]; // [A, B]
	me: {
		username: string;
		team: "A" | "B";
	};
	currentMove: zone_k;
	roundState?: RoundState;
	summary?: ArchiveEntry;
	gamemode: {
		gamemode: BidGamemode;
		player: string;
		team: "A" | "B";
	};
	requiredPoints: number;
	declarations: ExpDeclaration[];
	eloRange?: GameEvents.GameStarted["eloRange"];
	bids?: {
		stronger: BidGamemode[];
		multiplier?: "contre" | "recontre";
	};
	bidHistory: Array<[player: string, bid: BidGamemode | "contre" | "recontre"]>;
	order: zone_k[];
	zones: Record<zone_k, Zone>;
	dealing?: {
		dealer: zone_k;
		flights: DealFlight[];
	};
	roundCooldownEndsAt?: number;
	playedCards: PlayedCard[];
    gameEndedModalData?: {
		outcome: GameEvents.GameEnded["outcome"];
		oldRating: number;
		newRating: number;
    };
    ended?: boolean;
}

type zone_k = "east" | "west" | "north" | "south";

let nextDealFlightId = 0;
const dealTimers = new Map<string, ReturnType<typeof setTimeout>[]>();

/// Screen seats in turn order. Play goes counter-clockwise (R3), which on a
/// board drawn from south's point of view means the seat that plays after you
/// is on your right: south -> east -> north -> west.
function getDefaultOrder(): zone_k[] {
	return ["south", "east", "north", "west"];
}

export const BID_MAP: Record<BidGamemode | "contre" | "recontre", string> = {
	P: "Pass",
	C: "Clubs",
	D: "Diamonds",
	H: "Hearts",
	S: "Spades",
	N: "No Trumps",
	A: "All Trumps",
	contre: "Contre",
	recontre: "Recontre"
};

/* ---------------------------------------------
 * The game is a loop of rounds until one team reaches the required points to win (by default 151).
 * Each round is split into 2 logical stages: Bidding & Playing
 * If the bidding stage is successful, then we move to the Playing stage; Bidding -> Playing
 * The playing stage is a loop of 8 Tricks
 * A trick is the stage of the game when all the players play a card.
 * The player with the strongest hand collects all the points from the table.
 * After all 8 tricks finish, the round finishes and the next starts (if there isn't a game winner).
 * ----------------------------------------------*/

export const useGameStore = defineStore("game", () => {
	const games = ref<Record<string, Game>>({});
	const myUuid = ref<string>();

	const myGame = computed(() => {
		if (myUuid.value) {
			return games.value[myUuid.value];
		}
	});

	function getGameHandler(uuid: string) {
		const game = games.value[uuid];

		if (!game) return;

		return {
			// Game Events
            gameEnded(modalData?: { outcome: GameEvents.GameEnded["outcome"]; oldRating: number; newRating: number }) {
                game.ended = true;
				game.roundCooldownEndsAt = undefined;
				if (modalData && modalData.newRating !== -1) {
					game.gameEndedModalData = modalData;
				}
			},

			updatePoints(pointsA: number, pointsB: number) {
				game.points = [pointsA, pointsB];
			},
			// Round Events

			roundStarted(order: string[]) {
				for (const timer of dealTimers.get(uuid) ?? []) clearTimeout(timer);
				dealTimers.delete(uuid);

				// The server lists the opening bidder first. The seat immediately
				// before it is the dealer, which is the person cards originate from.
				const dealerName = order[order.length - 1];
				const dealer = dealerName ? this._getZoneNameByUsername(dealerName) : undefined;
				// clear messages & played cards
				for (const zone in game.zones) {
					game.zones[zone as zone_k] = {
						cards: [],
						username: game.zones[zone as zone_k].username,
						profile: game.zones[zone as zone_k].profile
					};
				}
				game.dealing = dealer ? { dealer, flights: [] } : undefined;
				game.roundCooldownEndsAt = undefined;
				game.roundState = undefined;
				game.bidHistory = [];
				game.order = order.map((username) => this._getZoneNameByUsername(username)!);
				// close summary
				game.summary = undefined;
			},

			roundEnded(summary: ArchiveEntry) {
				game.roundState = undefined;
				game.summary = summary;
				game.gamemode = {
					gamemode: "P",
					player: game.me.username,
					team: game.me.team
				};
			},

			roundStateChanged(state: RoundState) {
				game.roundState = state;
			},

			roundCooldown(durationMs: number) {
				game.roundCooldownEndsAt = Date.now() + durationMs;
			},

			dealCards(cards: string[] | number) {
				const length = typeof cards === "number" ? cards : cards.length;
				const dealer = game.dealing?.dealer ?? "north";
				// Cards are dealt in turn order too (R4), so this follows the seating.
				const seating = getDefaultOrder();
				const dealerIndex = seating.indexOf(dealer);
				const recipients = Array.from({ length: seating.length }, (_, index) =>
					seating[(dealerIndex + index + 1) % seating.length]
				);
				const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
				const step = reducedMotion ? 0 : 70;
				const travel = reducedMotion ? 0 : 380;
				const flights: DealFlight[] = [];
				const timers = dealTimers.get(uuid) ?? [];

				for (let cardIndex = 0; cardIndex < length; cardIndex++) {
					for (let recipientIndex = 0; recipientIndex < recipients.length; recipientIndex++) {
						const target = recipients[recipientIndex];
						const flight: DealFlight = {
							id: nextDealFlightId++,
							target,
							delay: 60 + (cardIndex * recipients.length + recipientIndex) * step
						};
						flights.push(flight);

						const card = target === "south" && typeof cards !== "number" ? cards[cardIndex] : "back";
						timers.push(setTimeout(() => {
							game.zones[target].cards.push(card);
							if (target === "south") {
								game.zones.south.cards.sort((a, b) => cardComparator(a, b, game.gamemode.gamemode || "A"));
							}
						}, flight.delay + travel));
						timers.push(setTimeout(() => {
							if (!game.dealing) return;
							game.dealing.flights = game.dealing.flights.filter(({ id }) => id !== flight.id);
							if (!game.dealing.flights.length) game.dealing = undefined;
						}, flight.delay + travel + 40));
					}
				}

				dealTimers.set(uuid, timers);
				game.dealing = { dealer, flights: [...(game.dealing?.flights ?? []), ...flights] };
			},

			declarationMade(player: string, declaration: Declaration) {
				const zone = this._getZoneByUsername(player);
				if (!zone) return;
				this._addZoneMessage(zone, declaration);
			},

			declarationRequested(declarations: ExpDeclaration[]) {
				game.declarations = declarations;
			},

			declarationFulfilled(declaration: ExpDeclaration) {
				ws.submitDeclaration(declaration);
				game.declarations.length = 0;
			},

			gamemodeSelected(gamemode: BidGamemode, player: string, team: "A" | "B") {
				game.gamemode = { gamemode, player, team };
			},

			// Bidding events

			bidSelected(player: string, bid: BidGamemode | "contre" | "recontre") {
				const zone = this._getZoneByUsername(player);
				if (!zone) {
					return;
				}
				game.bidHistory.push([player, bid]);
				this._addZoneMessage(zone, BID_MAP[bid] ?? bid);
			},

			bidRequested(stronger: BidGamemode[], multiplier?: "contre" | "recontre") {
				game.bids = {
					...game.bids,
					stronger
				};
				game.bids!.multiplier = multiplier;
			},

			fulfillBidRequest(gamemode: BidGamemode | "contre" | "recontre") {
				game.bids = undefined;
				ws.sendBidSelected(gamemode);
			},

			// Trick events

			trickStarted(order: string[]) {
				this._clearPlayedAndPlayableCards();
				game.order = order.map((username) => this._getZoneNameByUsername(username)!);
			},

			cardRequested(legalMoves: string[], time_ms: number) {
				game.zones.south.playableCards = legalMoves;
			},

			cardFulfilled(card: string) {
				ws.sendCardPlayed(card);
			},

			cardPlayed(player: string, card: string) {
				console.log("card played", game);
				const zone = this._getZoneByUsername(player);
				console.log("card played", zone);
				if (!zone) return;

				game.order.shift();
				zone.playedCard = card;
				if (zone.cards.some((card) => card === "back")) {
					zone.cards.pop();
				} else {
					zone.cards = zone.cards.filter((c) => c !== card);
				}
				zone.playableCards = undefined;
			},

			trickEnded(handholder: string, card: string) {
				this._clearPlayedAndPlayableCards();
			},

			// Helpers

			_getZoneByUsername(username: string) {
				for (const name in game.zones) {
					const zone = game.zones[name as zone_k];
					if (zone.username === username) {
						return zone;
					}
				}
			},

			_getZoneNameByUsername(username: string): zone_k | undefined {
				for (const name in game.zones) {
					const zone = game.zones[name as zone_k];
					if (zone.username === username) {
						return name as zone_k;
					}
				}
			},

			_addZoneMessage(zone: Zone, message: string) {
				zone.message = message;
				setTimeout(() => {
					zone.message = "";
				}, 2500);
			},

			_clearPlayedAndPlayableCards() {
				for (const name in game.zones) {
					const zone = game.zones[name as zone_k];
					zone.playedCard = undefined;
					zone.playableCards = undefined;
				}
			}
		};
	}

	// Game Events
	function gameStarted(
		uuid: string,
		username: string,
		team: "A" | "B",
		order: string[],
		requiredPoints: number,
		eloRange?: GameEvents.GameStarted["eloRange"]
	) {
		myUuid.value = uuid;

		const index = order.indexOf(username);
		const prevIndex = index == 0 ? 3 : index - 1;
		const nextIndex = index == 3 ? 0 : index + 1;
		const teammateIndex = index + (index < 2 ? 2 : -2);

		const game: Game = {
			points: [0, 0],
			me: {
				username,
				team
			},
			zones: {
				east: {
					username: order[nextIndex],
					cards: [],
					playedCard: "7c"
				},
				west: {
					username: order[prevIndex],
					cards: [],
					playedCard: "8c"
				},
				north: {
					username: order[teammateIndex],
					cards: [],
					playedCard: "9c"
				},
				south: {
					username: order[index],
					cards: [],
					playedCard: "10c"
				}
			},
			currentMove: "south",
			bidHistory: [],
			declarations: [],
			eloRange,
			requiredPoints,
			gamemode: {
				gamemode: "P",
				player: username,
				team: team
			},
			playedCards: [],
			order: getDefaultOrder()
		};

		// load profiles async
		http.getProfile(order[nextIndex]).then((prof) => (game.zones.east.profile = prof));

		http.getProfile(order[prevIndex]).then((prof) => (game.zones.west.profile = prof));

		http.getProfile(order[teammateIndex]).then((prof) => (game.zones.north.profile = prof));

		http.getProfile(order[index]).then((prof) => (game.zones.south.profile = prof));

		games.value[uuid] = game;
	}

	return {
		myUuid,
		myGame,
		games,
		gameStarted,
		getGameHandler
	};
});
